import _cloneDeep from "lodash.clonedeep";
import { createContext, Dispatch, useContext, useEffect, useReducer } from "react";
import useClientContext from "./useClientContext";
import useGame from "./useGame";
import usePrevious from "./usePrevious";
import { IPick, PlayoffsEntry } from "./useSession";

export const initialState: PicksState = {
  needsToSave: false,
  focusedColumn: 1,
  picks: {
    1: [
      { id: "1", pick: null },
      { id: "2", pick: null },
      { id: "3", pick: null },
      { id: "4", pick: null },
      { id: "5", pick: null },
      { id: "6", pick: null },
      { id: "7", pick: null },
      { id: "8", pick: null },
      { id: "9", pick: null },
      { id: "10", pick: null },
      { id: "11", pick: null },
      { id: "12", pick: null },
    ],
    2: [
      { id: "1", pick: null },
      { id: "2", pick: null },
      { id: "3", pick: null },
      { id: "4", pick: null },
      { id: "5", pick: null },
      { id: "6", pick: null },
      { id: "7", pick: null },
      { id: "8", pick: null },
    ],
    3: [
      { id: "1", pick: null },
      { id: "2", pick: null },
      { id: "3", pick: null },
      { id: "4", pick: null },
    ],
    4: [{ id: "1", pick: null }],
  },
};

const PicksContext = createContext<TPicksContext>([initialState, null]);

function picksReducer(state: PicksState, action: PickAction): PicksState {
  switch (action.type) {
    case ACTIONS.UPDATE_PICKS: {
      return { ...state, picks: action.picks, needsToSave: action.shouldPromptSave };
    }
    case ACTIONS.UPDATE_NEEDS_SAVE: {
      return { ...state, needsToSave: action.needsToSave };
    }
    case ACTIONS.SET_FOCUSED_COLUMN: {
      return { ...state, focusedColumn: action.focusedColumn };
    }
    default: {
      throw new Error(`Unhandled action in picksReducer: ${action}`);
    }
  }
}

/**
 * Picks UI state context provider.
 */
export function PicksProvider({ children }) {
  const [state, dispatch] = useReducer(picksReducer, initialState);
  const { needsToSave } = state;

  const [{ currentEntry }] = useClientContext();
  const { data: game } = useGame();
  const currentPicks = currentEntry?.picks;
  const currentId = currentEntry?.id;
  const currentRound = game.state?.current_round ?? 1;

  // track the prev entryId using a ref.
  const prevEntryId = usePrevious(currentEntry?.id);

  useEffect(() => {
    // Update picks state when there's an entryId change.
    if (!prevEntryId || prevEntryId !== currentId) {
      const newStatePicks = formatPicks(currentPicks);
      dispatch(updatePicks(currentId ? newStatePicks : initialState.picks, false));
    }
  }, [prevEntryId, needsToSave, currentPicks, currentId]);

  useEffect(() => {
    dispatch(setFocusedColumn(currentRound));
  }, [currentRound]);

  return <PicksContext.Provider value={[state, dispatch]}>{children}</PicksContext.Provider>;
}

/**
 * Main hook to useContext for picks state and dispatching changes.
 * @example const [state, dispatch] = usePicksContext()
 */
export default function usePicksContext(): TPicksContext {
  const context = useContext<TPicksContext>(PicksContext);
  if (context === undefined) {
    throw new Error("usePicksContext must be used within a PicksContext.Provider");
  }
  return context;
}

type TPicksContext = [PicksState, Dispatch<PickAction>];
export type PicksState = {
  needsToSave: boolean;
  focusedColumn: number;
  picks: {
    1: [Slot, Slot, Slot, Slot, Slot, Slot, Slot, Slot, Slot, Slot, Slot, Slot];
    2: [Slot, Slot, Slot, Slot, Slot, Slot, Slot, Slot];
    3: [Slot, Slot, Slot, Slot];
    4: [Slot];
  };
};

export type Slot = {
  id: string;
  pick: IPick;
  points?: number;
};

enum ACTIONS {
  UPDATE_PICKS = "UPDATE_PICKS",
  UPDATE_NEEDS_SAVE = "UPDATE_NEEDS_SAVE",
  SET_FOCUSED_COLUMN = "SET_FOCUSED_COLUMN",
}

interface UpdatePicks {
  type: ACTIONS.UPDATE_PICKS;
  picks: PicksState["picks"];
  shouldPromptSave: boolean;
}

interface UpdateNeedsToSave {
  type: ACTIONS.UPDATE_NEEDS_SAVE;
  needsToSave: boolean;
}

interface SetFocusedColumn {
  type: ACTIONS.SET_FOCUSED_COLUMN;
  focusedColumn: number;
}

type PickAction = UpdatePicks | UpdateNeedsToSave | SetFocusedColumn;

export const updatePicks = (picks: PicksState["picks"], shouldPromptSave = true): UpdatePicks => ({
  type: ACTIONS.UPDATE_PICKS,
  picks: { ...picks },
  shouldPromptSave,
});

export const updateNeedsToSave = (needsToSave: boolean): UpdateNeedsToSave => ({
  type: ACTIONS.UPDATE_NEEDS_SAVE,
  needsToSave,
});

export const setFocusedColumn = (focusedColumn: number): SetFocusedColumn => ({
  type: ACTIONS.SET_FOCUSED_COLUMN,
  focusedColumn,
});

/**
 * Utility Fn to format session picks into application state picks.
 */
export function formatPicks(picks: PlayoffsEntry["picks"]): PicksState["picks"] {
  const newPicks = _cloneDeep(initialState.picks);

  if (!picks) return newPicks;

  for (const [key, value] of Object.entries(picks)) {
    const newValue = value.map(
      (pick, index): Slot => ({
        id: `${index + 1}`,
        pick: typeof pick !== "object" ? pick : pick?.driver_id,
        points: typeof pick === "object" ? pick?.points : null,
      }),
    );
    newPicks[key] = newValue;
  }
  return newPicks;
}
